<template>
  <div
    class="border border-gray-200 rounded-lg hover:shadow-md transition-shadow duration-200"
  >
    <div class="p-4">
      <div
        class="flex justify-between items-center mb-4 pb-3 border-b border-gray-100"
      >
        <span class="font-bold text-blue-500 text-sm"
          >{{ t('unpaid_orders.order_code') }}: {{ item.bookingCode }}</span
        >
        <span class="font-bold text-primary text-base">{{
          formatCurrency(item.totalAmount)
        }}</span>
      </div>

      <div class="space-y-2 mb-4">
        <div class="flex justify-between text-sm">
          <span class="text-gray-600 font-medium"
            >{{ t('unpaid_orders.created_time') }}:</span
          >
          <span class="text-gray-800 font-semibold">{{
            formatDate(item.created_at || '')
          }}</span>
        </div>

        <div class="flex justify-between text-sm">
          <span class="text-gray-600 font-medium">
            {{ t('payment_waiting.qr_section.time_remaining') }}
          </span>
          <span class="text-green-700 font-bold">
            {{ formatTime(timeLeft) }}
          </span>
        </div>

        <div class="flex justify-between text-sm">
          <span class="text-gray-600 font-medium"
            >{{ t('unpaid_orders.seat_count') }}:</span
          >
          <span class="text-gray-800 font-semibold"
            >{{ item.numberOfSeats }}
            {{ t('unpaid_orders.seats_suffix') }}</span
          >
        </div>
      </div>

      <div class="flex gap-2 justify-end">
        <el-button
          :disabled="timeLeft === 0"
          @click="handleToPayment(item.bookingCode)"
          type="primary"
          size="small"
        >
          {{ t('unpaid_orders.pay_now') }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
const props = defineProps({
  item: {
    type: Object,
    default: () => {},
  },
})

const { t, locale } = useI18n()
const router = useRouter()
const show = ref(true)

const handleToPayment = (item: string) => {
  show.value = false
  router.replace({
    path: convertRoute('/payment'),
    query: { booking: item },
  })
}
const targetTime = ref<Date | null>(null)
const timeLeft = ref(0)
let timer: any = null

const formatTime = (seconds: number) => {
  if (seconds <= 0) return '00:00'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

// Format currency
const formatCurrency = (amount: any) => {
  return new Intl.NumberFormat(locale.value === 'vi' ? 'vi-VN' : 'en-US', {
    style: 'currency',
    currency: 'VND',
  }).format(amount)
}

const handleRemoveBooking = () => {
  const raw = localStorage.getItem('bookings')
  const data: any[] = raw ? JSON.parse(raw) : []

  const i = data.filter((item) => item.bookingCode !== props.item.bookingCode)

  localStorage.setItem('bookings', JSON.stringify(i))
}

const updateCountdown = () => {
  if (!targetTime.value) return
  const now = new Date().getTime()
  const diff = Math.floor((targetTime.value.getTime() - now) / 1000)

  if (diff <= 0) {
    timeLeft.value = 0
    clearInterval(timer)
    handleRemoveBooking()
  } else {
    timeLeft.value = diff
  }
}

const startTimer = () => {
  updateCountdown()
  timer = setInterval(updateCountdown, 1000)
}

// on mo
onMounted(() => {
  if (props.item?.holdExpiresAt) {
    targetTime.value = new Date(props.item.holdExpiresAt)
  } else {
    targetTime.value = new Date(Date.now() + 4 * 60 * 1000)
  }
  startTimer()
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
})

// Format date
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString(
    locale.value === 'vi' ? 'vi-VN' : 'en-US',
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    },
  )
}

// convert route
const convertRoute = (path: string) =>
  locale.value === 'en' ? `/${locale.value}${path}` : path
</script>
